import { createSelector } from '@reduxjs/toolkit';
import type { AppDispatch } from '../../app/app-store';
import {
  fetchCompanyDocumentTypes,
  selectCompanyDocumentTypes,
} from '../../features/api/slices/company-document-types-slice';
import {
  fetchOutdatedCompanyDocuments,
  selectOutdatedCompanyDocuments,
} from '../../features/api/slices/company-documents-slice';
import {
  fetchGinStepDocumentRequests,
  selectGinStepDocumentRequests,
} from '../../features/api/slices/gin-steps-slice';
import {
  fetchCompanyTourSteps,
  selectCompanyTourSteps,
} from '../../features/api/slices/profile-slice';
import type ClientApi from '../../features/api/client-api';
import type ClientApiV2 from '../../features/api/client-api-v2';
import {
  dashboardApplicationDocumentRequestFromPersonalRequest,
  deduplicateDashboardApplicationDocumentRequests,
} from './application-document-request-projection';
import { buildDashboardCompanyDocumentRequests } from './company-document-requests';
import type { DashboardDocumentRequest } from './document-requests-widget';

export const loadDocumentRequests = ({
  dispatch,
  api,
  newApi,
  locale,
}: {
  dispatch: AppDispatch;
  api: ClientApi;
  newApi: ClientApiV2;
  locale: string;
}) =>
  Promise.all([
    dispatch(fetchGinStepDocumentRequests({ api, locale })),
    dispatch(fetchOutdatedCompanyDocuments({ api: newApi, locale })),
    dispatch(fetchCompanyTourSteps({ api, locale })),
    dispatch(fetchCompanyDocumentTypes({ api: newApi, locale })),
  ]).then(() => undefined);

export const selectDashboardDocumentRequests = createSelector(
  [
    selectGinStepDocumentRequests,
    selectCompanyTourSteps,
    selectCompanyDocumentTypes,
    selectOutdatedCompanyDocuments,
  ],
  (
    ginStepDocumentRequests,
    companyTourSteps,
    companyDocumentTypes,
    outdatedCompanyDocuments,
  ): DashboardDocumentRequest[] => {
    const applicationRequests = ginStepDocumentRequests.map(
      dashboardApplicationDocumentRequestFromPersonalRequest,
    );
    const uniqueApplicationRequests =
      deduplicateDashboardApplicationDocumentRequests(applicationRequests);

    return [
      ...uniqueApplicationRequests,
      ...buildDashboardCompanyDocumentRequests({
        companyTourSteps,
        companyDocumentTypes,
        outdatedCompanyDocuments,
      }),
    ];
  },
);
